import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface CustomizationOption {
  id: string;
  name: string;
  price_type: string;
  price_amount: number;
}

interface Customization {
  id: string;
  name: string;
  type: string;
  is_required: boolean;
  options: CustomizationOption[];
}

interface MenuItem {
  id: string;
  name: string;
  description?: string;
  price: number;
  image_url: string;
  restaurant_id: string;
  restaurant_name: string;
  category_id?: string;
  is_customizable?: boolean;
}

interface SelectedCustomization {
  customization_id: string;
  selected_options: string[];
  text_input?: string;
}

interface ItemCustomizationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  menuItem: MenuItem | null;
  onAddToCart: (item: any, customizations: SelectedCustomization[]) => void;
}

export const ItemCustomizationDialog = ({ 
  isOpen, 
  onClose, 
  menuItem, 
  onAddToCart 
}: ItemCustomizationDialogProps) => {
  const [customizations, setCustomizations] = useState<Customization[]>([]);
  const [selectedCustomizations, setSelectedCustomizations] = useState<SelectedCustomization[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && menuItem) {
      fetchCustomizations();
    }
  }, [isOpen, menuItem]);

  const fetchCustomizations = async () => {
    if (!menuItem) return;
    
    setLoading(true);
    try {
      // Fetch customizations for this menu item
      const { data: customizationData, error } = await supabase
        .from('menu_item_customizations')
        .select(`
          id,
          name,
          type,
          is_required,
          customization_options (
            id,
            name,
            price_type,
            price_amount
          )
        `)
        .eq('menu_item_id', menuItem.id);

      if (error) throw error;

      const formattedCustomizations = customizationData?.map(c => ({
        id: c.id,
        name: c.name,
        type: c.type,
        is_required: c.is_required,
        options: c.customization_options || []
      })) || [];

      setCustomizations(formattedCustomizations);
      
      // Initialize selected customizations
      const initialSelections = formattedCustomizations.map(c => ({
        customization_id: c.id,
        selected_options: [],
        text_input: ''
      }));
      setSelectedCustomizations(initialSelections);
    } catch (error) {
      console.error('Error fetching customizations:', error);
      toast.error('Failed to load customization options');
    } finally {
      setLoading(false);
    }
  };

  const updateSelection = (customizationId: string, optionId?: string, textValue?: string) => {
    setSelectedCustomizations(prev => 
      prev.map(sc => {
        if (sc.customization_id === customizationId) {
          const customization = customizations.find(c => c.id === customizationId);
          
          if (customization?.type === 'text_input') {
            return { ...sc, text_input: textValue };
          } else if (customization?.type === 'single_select' && optionId) {
            return { ...sc, selected_options: [optionId] };
          } else if (customization?.type === 'multiple_select' && optionId) {
            const currentOptions = sc.selected_options || [];
            const isSelected = currentOptions.includes(optionId);
            return { 
              ...sc, 
              selected_options: isSelected 
                ? currentOptions.filter(id => id !== optionId)
                : [...currentOptions, optionId]
            };
          }
        }
        return sc;
      })
    );
  };

  const validateSelections = () => {
    for (const customization of customizations) {
      if (customization.is_required) {
        const selection = selectedCustomizations.find(sc => sc.customization_id === customization.id);
        
        if (customization.type === 'text_input') {
          if (!selection?.text_input?.trim()) {
            toast.error(`${customization.name} is required`);
            return false;
          }
        } else if (!selection?.selected_options?.length) {
          toast.error(`Please select an option for ${customization.name}`);
          return false;
        }
      }
    }
    return true;
  };

  const calculateTotalPrice = () => {
    if (!menuItem) return 0;
    
    let totalPrice = menuItem.price;
    
    selectedCustomizations.forEach(sc => {
      const customization = customizations.find(c => c.id === sc.customization_id);
      if (customization) {
        sc.selected_options?.forEach(optionId => {
          const option = customization.options.find(o => o.id === optionId);
          if (option && option.price_type === 'fixed') {
            totalPrice += option.price_amount;
          }
        });
      }
    });
    
    return totalPrice;
  };

  const handleAddToCart = () => {
    if (!menuItem) return;
    
    if (!validateSelections()) return;
    
    const finalPrice = calculateTotalPrice();
    const itemWithPrice = { ...menuItem, price: finalPrice };
    
    onAddToCart(itemWithPrice, selectedCustomizations);
    onClose();
  };

  const getSelectedOption = (customizationId: string) => {
    const selection = selectedCustomizations.find(sc => sc.customization_id === customizationId);
    return selection?.selected_options?.[0] || '';
  };

  const isOptionSelected = (customizationId: string, optionId: string) => {
    const selection = selectedCustomizations.find(sc => sc.customization_id === customizationId);
    return selection?.selected_options?.includes(optionId) || false;
  };

  const getTextValue = (customizationId: string) => {
    const selection = selectedCustomizations.find(sc => sc.customization_id === customizationId);
    return selection?.text_input || '';
  };

  if (!menuItem) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md mx-auto max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Customize {menuItem.name}</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-red-600 border-t-transparent"></div>
          </div>
        ) : (
          <div className="space-y-6">
            {customizations.map((customization) => (
              <div key={customization.id} className="space-y-3">
                <Label className="text-base font-semibold">
                  {customization.name}
                  {customization.is_required && <span className="text-red-500 ml-1">*</span>}
                </Label>

                {customization.type === 'text_input' ? (
                  <Input
                    placeholder={`Enter ${customization.name.toLowerCase()}`}
                    value={getTextValue(customization.id)}
                    onChange={(e) => updateSelection(customization.id, undefined, e.target.value)}
                  />
                ) : customization.type === 'single_select' ? (
                  <RadioGroup
                    value={getSelectedOption(customization.id)}
                    onValueChange={(value) => updateSelection(customization.id, value)}
                  >
                    {customization.options.map((option) => (
                      <div key={option.id} className="flex items-center space-x-2">
                        <RadioGroupItem value={option.id} id={option.id} />
                        <Label htmlFor={option.id} className="flex-1 cursor-pointer">
                          <div className="flex justify-between">
                            <span>{option.name}</span>
                            {option.price_type === 'fixed' && option.price_amount > 0 && (
                              <span className="text-red-600 font-semibold">+D{option.price_amount}</span>
                            )}
                          </div>
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                ) : customization.type === 'multiple_select' ? (
                  <div className="space-y-2">
                    {customization.options.map((option) => (
                      <div key={option.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={option.id}
                          checked={isOptionSelected(customization.id, option.id)}
                          onCheckedChange={() => updateSelection(customization.id, option.id)}
                        />
                        <Label htmlFor={option.id} className="flex-1 cursor-pointer">
                          <div className="flex justify-between">
                            <span>{option.name}</span>
                            {option.price_type === 'fixed' && option.price_amount > 0 && (
                              <span className="text-red-600 font-semibold">+D{option.price_amount}</span>
                            )}
                          </div>
                        </Label>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            ))}

            <div className="border-t pt-4">
              <div className="flex justify-between items-center mb-4">
                <span className="text-lg font-semibold">Total:</span>
                <span className="text-xl font-bold text-red-600">D{calculateTotalPrice()}</span>
              </div>
              
              <div className="flex gap-3">
                <Button variant="outline" onClick={onClose} className="flex-1">
                  Cancel
                </Button>
                <Button onClick={handleAddToCart} className="flex-1 bg-red-600 hover:bg-red-700">
                  Add to Cart
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};